import { gql } from 'graphql-request';

// Sprint 25, story-128 (run 5). `linkedUser`/`accessSettings` were added to `ClientDto` by
// earlier runs (see `backend/src/entities/client/client.resolver.ts` `@ResolveField`s) but no
// web query ever asked for them until now. `notifyTaskStatus`/`notifyDocuments` are
// deliberately NOT fetched: there is no CLIENT notification path, so the web UI never renders
// them as toggles.
export const CLIENT_FIELDS = gql`
  fragment ClientFields on ClientDto {
    clientId
    name
    address
    phone
    email
    logo
    projects {
      projectId
      name
      clientId
      address
      budget
      startDate
      endDate
      state
      siteImage
      clientId
    }
    linkedUser {
      email
      status
      invitedAt
      lastSignInAt
    }
    accessSettings {
      tasks
      scheduleDates
      documents
      evidence
      notes
      activity
      incidents
      materials
      equipment
      priceTotals
    }
  }
`;

export const getClients = gql`
  query getClients {
    getClients {
      ...ClientFields
    }
  }

  ${CLIENT_FIELDS}
`;

export const createClient = gql`
  mutation createClient($client: ClientCreateInput!) {
    createClient(client: $client) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

export const updateClient = gql`
  mutation updateClient($client: ClientUpdateInput!) {
    updateClient(client: $client) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

export const deleteClient = gql`
  mutation deleteClient($id: String!) {
    deleteClient(id: $id) {
      affected
      messages
    }
  }
`;

export const getClientByID = gql`
  query getClientByID($id: String!) {
    getClientByID(id: $id) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

// Sprint 25, story-128 (run 5). Matches `client.resolver.ts` `inviteClientUser` /
// `removeClientAccess` / `updateClientAccessSetting`.
export const inviteClientUser = gql`
  mutation inviteClientUser($clientId: String!, $resend: Boolean) {
    inviteClientUser(clientId: $clientId, resend: $resend) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

export const removeClientAccess = gql`
  mutation removeClientAccess($clientId: String!) {
    removeClientAccess(clientId: $clientId) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

export const updateClientAccessSetting = gql`
  mutation updateClientAccessSetting($clientId: String!, $key: ClientAccessSettingKey!, $value: Boolean!) {
    updateClientAccessSetting(clientId: $clientId, key: $key, value: $value) {
      ...ClientFields
    }
  }
  ${CLIENT_FIELDS}
`;

// Sprint 43, story-222/223. A CLIENT's own checklist. `notifyTaskStatus`/`notifyDocuments` are not
// fetched, for the same reason `CLIENT_FIELDS` leaves them out (no notification path exists).
export const myClientAccess = gql`
  query myClientAccess {
    myClientAccess {
      clientId
      clientName
      organizationName
      settings {
        tasks
        scheduleDates
        documents
        evidence
        notes
        activity
        incidents
        materials
        equipment
        priceTotals
      }
    }
  }
`;

// Sprint 44, story-225/226. The always-visible progress tier: exactly the DTO fields, no money.
export const getClientProjectsOverview = gql`
  query getClientProjectsOverview($projectIds: [String!]) {
    getClientProjectsOverview(projectIds: $projectIds) {
      projectId
      projectName
      state
      isBehind
      health
      completion
      countableCount
      completeCount
      overdueCount
      dueThisWeekCount
      awaitingClientApprovalCount
      openIncidentsCount
      startDate
      endDate
      nextMilestones {
        taskId
        name
        date
        kind
      }
      lastActivityAt
    }
  }
`;

const ClientsGraphQL = {
  queries: {
    getClients,
    getClientByID,
    myClientAccess,
    getClientProjectsOverview,
  },
  mutations: {
    createClient,
    updateClient,
    deleteClient,
    inviteClientUser,
    removeClientAccess,
    updateClientAccessSetting,
  },
};

export default ClientsGraphQL;
